import FeatureFilter from '@arcgis/core/layers/support/FeatureFilter';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';

import type { ArcgisMapView } from '../LayersTab/utils/arcgisView';
import {
  symbolVisibleForGraphic,
  type AssetSymbolTarget,
} from './assetSymbolMatch';

export type { AssetSymbolTarget } from './assetSymbolMatch';

const ownedFeatureFilters = new WeakSet<object>();
/**
 * Graphics this helper hid; only these are shown again, so other code's hiding
 * is left alone.
 */
const hiddenGraphics = new WeakSet<object>();

function collectFeatureLayers(layers: __esri.Layer[]): FeatureLayer[] {
  const out: FeatureLayer[] = [];
  const visit = (layer: __esri.Layer) => {
    if (layer instanceof FeatureLayer) {
      out.push(layer);
      return;
    }
    if (layer instanceof GroupLayer) {
      for (const child of layer.layers.toArray()) visit(child);
    }
  };
  layers.forEach(visit);
  return out;
}

function graphicKeys(attributes: Record<string, unknown> | null | undefined) {
  if (!attributes) return [];
  return ['id', 'resource_set_id', 'resourceSetId', 'name']
    .map((key) => attributes[key])
    .filter((value) => value != null && String(value).trim() !== '')
    .map((value) => String(value));
}

function collectGraphicsLayers(layers: __esri.Layer[]): GraphicsLayer[] {
  const out: GraphicsLayer[] = [];
  const visit = (layer: __esri.Layer) => {
    if (layer instanceof GraphicsLayer) out.push(layer);
    if (layer instanceof GroupLayer) {
      for (const child of layer.layers.toArray()) visit(child);
    }
  };
  layers.forEach(visit);
  return out;
}

/**
 * Show or hide asset symbols already drawn as graphics. Unrelated graphics stay
 * as they are.
 */
export function applyAssetGraphicsVisibility(
  mapView: ArcgisMapView | null,
  targets: AssetSymbolTarget[],
  hiddenResourceSetIds: ReadonlySet<string>
) {
  if (!mapView) return;
  const graphics = [
    ...mapView.graphics.toArray(),
    ...collectGraphicsLayers(mapView.map?.layers.toArray() ?? []).flatMap(
      (layer) => layer.graphics.toArray()
    ),
  ];
  for (const graphic of graphics) {
    const next = symbolVisibleForGraphic(
      graphicKeys(graphic.attributes as Record<string, unknown> | null),
      targets,
      hiddenResourceSetIds
    );
    if (next === false) {
      if (graphic.visible) {
        graphic.visible = false;
        hiddenGraphics.add(graphic);
      }
    } else if (hiddenGraphics.has(graphic)) {
      graphic.visible = true;
      hiddenGraphics.delete(graphic);
    }
  }
}

/** Hide ArcGIS-linked asset features. Clears only filters this helper installed. */
export async function applyAssetFeatureVisibility(
  mapView: ArcgisMapView | null,
  targets: AssetSymbolTarget[],
  hiddenResourceSetIds: ReadonlySet<string>
) {
  if (!mapView?.map) return;
  const hiddenByLayer = new Map<string, number[]>();
  for (const target of targets) {
    if (
      target.objectId == null ||
      !target.layerId ||
      !hiddenResourceSetIds.has(target.resourceSetId)
    ) {
      continue;
    }
    const list = hiddenByLayer.get(target.layerId) ?? [];
    list.push(target.objectId);
    hiddenByLayer.set(target.layerId, list);
  }

  const layers = collectFeatureLayers(mapView.map.layers.toArray());
  await Promise.all(
    layers.map(async (layer) => {
      if (!(layer instanceof FeatureLayer)) return;
      let layerView: __esri.FeatureLayerView;
      try {
        layerView = (await mapView.whenLayerView(
          layer
        )) as __esri.FeatureLayerView;
      } catch {
        return;
      }
      const ids = hiddenByLayer.get(layer.id) ?? [];
      if (ids.length === 0) {
        if (ownedFeatureFilters.has(layerView)) {
          layerView.filter = null;
          ownedFeatureFilters.delete(layerView);
        }
        return;
      }
      const where = [...new Set(ids)]
        .map((id) => `OBJECTID <> ${Math.trunc(id)}`)
        .join(' AND ');
      layerView.filter = new FeatureFilter({ where });
      ownedFeatureFilters.add(layerView);
    })
  );
}
